(ns app.front.ui.multi-select
  (:require [app.front.ui.util :as uiu]
            [app.front.util :refer [for-frag] :as futil]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [schema-tools.core :as st]))


(defn- ->item [val->str [token {:keys [value]}]]
  {:id    token
   :label (val->str value)})


(def wrap-text-classes
  (->> ["content-center" ; Needed for inline + wrap combination to keep alignment correct
        cc/carbon-checkbox-wrap-label-class
        cc/carbon-checkbox-auto-height-class
        cc/carbon-checkbox-center-pseudo-classes]
       (str/join " ")))


(defn- base-multi-select
  [component
   schema
   selection-key
   {:keys [values val->str val->token on-change value label-text hide-label? id wrap-text?] :as props
    :or   {val->str str}}]
  (let [value        (set value)
        token->value (uiu/make-token->value false val->token values)
        ;; A vector makes even an empty selection a JS array; nil would fall back to Carbon's
        ;; uncontrolled selection.
        curr-value   (->> token->value
                          (filter (comp (or value #{}) :value second))
                          (map (partial ->item val->str))
                          (into []))]
    [:div.multi-select {:class (if hide-label? "multi-select--label-hidden" "")}
     (component (-> (merge props
                           {:key          (str id "-" (hash values))
                            :items        (->> token->value
                                               uiu/sort-tokens
                                               (map (partial ->item val->str)))
                            :title-text   (or label-text "")
                            selection-key curr-value
                            :on-change    (fn [e]
                                            (->> (-> e js->clj (get "selectedItems"))
                                                 (map #(get % "id"))
                                                 (map token->value)
                                                 (map :value)
                                                 set
                                                 on-change))})
                    (p/assoc-if :class-name (futil/cs (:class-name props)
                                                      (when wrap-text? wrap-text-classes)))
                    (dissoc :wrap-text?)
                    (st/select-schema schema)))]))


(defn multi-select
  "Make a select component that operates on a sequence of arbitrary values (not just
  strings) and allows multiple options to be selected.

  Nil represents no selected values and is permitted.
  Otherwise the value should be a set.
  Props are passed through to `cc/multi-select`

  The selection is controlled by `:value`: the component re-renders the selection when the
  value changes, so callers must update `:value` in `:on-change`.

  Required
   `:id`, `:label-text`, `:values`, `:val->str`, `:on-change`, `:value`"
  [{:keys [displayed-text label-text] :as props}]
  (base-multi-select cc/multi-select
                     cc/MultiSelectOptions
                     :selected-items
                     (assoc props :label (or displayed-text
                                             label-text
                                             ""))))


(defn filterable-multi-select
  "Similar to multi-select but with a filter input to narrow down the options.
  FilterableMultiSelect does not support controlled selection, so it seeds the selection
  with `:initial-selected-items` and keeps it internally."
  [props]
  (base-multi-select cc/filterable-multi-select
                     cc/FilterableMultiSelectOptions
                     :initial-selected-items
                     props))


(defn multi-checkbox
  "A group of checkboxes that alter the same data saved as a set and rendered in a grid with buttons to
  automatically select all or none of the checkboxes.

  Required
  :tr, :values, :selected, :val->str, :on-change"
  [{:keys [id tr values selected val->str val->token on-change disabled read-only? required label-text loading? all-toggle?] :or {all-toggle? true}}]
  (let [token->value (uiu/make-token->value false val->token values)
        values-set   (set values)
        disabled     (set disabled)]
    [:<>
     (when label-text
       [:div.flex.gap-6.pb-2
        [:h4 {:class (when required "required-field")} label-text]
        (when loading?
          (cc/loading-spinner {:small true :with-overlay false}))])
     [:div.grid.grid-cols-4.gap-2
      {:class cc/carbon-checkbox-no-margin-class}
      (for-frag [[token {:keys [value]}] (uiu/sort-tokens token->value)]
        (let [checked  (contains? selected value)
              disabled (or read-only?
                           (contains? disabled value))]
          (cc/checkbox (util/mmap {:id         (str id "-" token)
                                   :label-text (val->str value)
                                   :on-change  #(on-change (if checked
                                                             (disj selected value)
                                                             (conj selected value)))}
                                  checked disabled))))]
     (when-not read-only?
       [:div.pt-2.flex.flex-row
        (when all-toggle?
          [:div
           (cc/button {:id       "choose-no-values-button"
                       :data-cy  "choose-no-values-button"
                       :disabled (empty? selected)
                       :icon     i/remove
                       :size     :small
                       :on-click #(on-change (set/intersection selected disabled))}
                      (tr [:multi-select/select-none]))
           (cc/button {:id         "choose-all-values-button"
                       :data-cy    "choose-all-values-button"
                       :disabled   (= values-set selected)
                       :icon       i/check
                       :size       :small
                       :class-name "ml-2"
                       :on-click   (fn [_]
                                     (let [currently-deselected (set/difference values-set
                                                                                selected)
                                           cannot-be-deselected (set/intersection currently-deselected
                                                                                  disabled)]
                                       (on-change (set/difference values-set
                                                                  cannot-be-deselected))))}
                      (tr [:multi-select/select-all]))])
        (when (and (nil? label-text) loading?)
          [:div.ml-2.mt-2
           (cc/loading-spinner {:small true :with-overlay false})])])]))
